import styled, { css, keyframes } from "styled-components";

import cursorPointer from "../../assets/cursors/cursor_pointer.png";
import ButtonComponent from "../Button";
import { AbsoluteCentered } from "../Layout";

export const Container = styled.div`
  position: relative;
  display: flex;
`;

const AspectRatio = css`
  aspect-ratio: 16 / 9; // this should equal the aspect ratio of the "kast" image
  width: 100vw;
  height: auto; // aspect-ratio only works if either width or height is "auto"
  justify-content: space-evenly;

  @media (min-aspect-ratio: 16 / 9) {
    /* Flip the calculation if the screen is above 16:9 */
    & {
      height: 100vh;
      width: auto;
    }
  }
`;

export const AbsoluteImagesContainer = styled(AbsoluteCentered)`
  margin: initial;
  height: 100vh;
`;

export const Images = styled.div`
  margin: auto;
  display: flex;
  z-index: -1;
  ${AspectRatio};
`;

export const Buttons = styled.div`
  margin: auto;
  display: flex;
  ${AspectRatio}
  cursor: url(${cursorPointer}) 28 0, pointer;
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  
  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  
  100% {
    opacity: 0;
  }
`;

const NUMBER_OF_WORDS = 3;
export const FADE_ANIMATION_DURATION_SECONDS = 0.5;
export const FADE_ANIMATION_DELAY_SECONDS = 0.5;

export const TOTAL_FADE_ANIMATION_DURATION_SECONDS =
  NUMBER_OF_WORDS * FADE_ANIMATION_DURATION_SECONDS +
  FADE_ANIMATION_DELAY_SECONDS;

type ImageWrapperProps = { readonly shouldFadeOut?: boolean };

export const ImageWrapper = styled.div<ImageWrapperProps>`
  width: 20%;
  margin-top: 2%;
  margin-left: 1%;
  opacity: 0;
  animation: ${fadeIn} ${FADE_ANIMATION_DURATION_SECONDS}s forwards;
  background-color: #bfe28a;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ shouldFadeOut }: ImageWrapperProps) =>
    shouldFadeOut &&
    css`
      animation: ${fadeOut} ${FADE_ANIMATION_DURATION_SECONDS}s backwards;
    `}

  // left
  &:nth-of-type(1) {
    animation-delay: ${FADE_ANIMATION_DELAY_SECONDS}s;
  }

  // mid
  &:nth-of-type(2) {
    animation-delay: ${FADE_ANIMATION_DELAY_SECONDS * 2}s;
  }

  // right
  &:nth-of-type(3) {
    animation-delay: ${FADE_ANIMATION_DELAY_SECONDS * 3}s;
    margin-left: 0;
  }
`;

export const Image = styled.img`
  object-fit: contain;
  object-position: center;
  width: 90%;
`;

const doors = (index: number) => {
  switch (index) {
    // left
    case 0:
      return css`
        margin-top: -13%;
        margin-left: -12%;
        width: 125%;
      `;
    // mid
    case 1:
      return css`
        margin-top: -10%;
        margin-left: -11%;
        width: 137%;
      `;
    // right
    case 2:
    default:
      return css`
        margin-top: -7%;
        margin-left: -14%;
        width: 162%;
      `;
  }
};

type DoorProps = {
  readonly isVisible: boolean;
  readonly index: number;
};

export const Door = styled.img<DoorProps>`
  object-fit: contain;
  object-position: center;
  height: auto;
  margin-top: 2%;
  margin-left: 1%;

  display: ${({ isVisible }: DoorProps) => (isVisible ? "block" : "none")};

  ${({ index }: DoorProps) => doors(index)}
`;

export const Button = styled.button`
  position: relative;
  appearance: none;
  border: none;
  background: none;
  width: 20%;
  margin-top: 2%;
  margin-left: 1%;
`;

const goldGlow = keyframes`
  0% {
    filter: drop-shadow(0 0 10px #ffffff) drop-shadow(0 0 20px #ffffff) drop-shadow(0 0 30px #ffffff) drop-shadow(0 0 40px #ffdd1b) drop-shadow(0 0 80px #ffdd1b) drop-shadow(0 0 100px #ffdd1b) ;
  }
  
  100% {
    filter: drop-shadow(0 0 5px #ffffff) drop-shadow(0 0 10px #ffffff) drop-shadow(0 0 15px #ffffff) drop-shadow(0 0 20px #ffdd1b) drop-shadow(0 0 40px #ffdd1b) drop-shadow(0 0 50px #ffdd1b) ;
  }
`;

type TrophyProps = {
  readonly isVisible: boolean;
  readonly isAnimating: boolean;
  readonly index: number;
};

export const Trophy = styled.img<TrophyProps>`
  font-size: 10vw;
  position: absolute;
  bottom: 23%;
  left: 0;
  right: 0;
  margin: auto;
  will-change: filter, drop-shadow;
  visibility: ${({ isVisible }: TrophyProps) =>
    isVisible ? "visible" : "hidden"};
  width: 75%;
  height: auto;
  object-fit: contain;
  object-position: bottom;
  filter: drop-shadow(0 0 5px #ffffff) drop-shadow(0 0 10px #ffffff)
    drop-shadow(0 0 15px #ffffff) drop-shadow(0 0 20px #ffdd1b)
    drop-shadow(0 0 40px #ffdd1b) drop-shadow(0 0 50px #ffdd1b);

  ${({ isAnimating }: TrophyProps) =>
    isAnimating &&
    css`
      animation: ${goldGlow} 0.75s ease-in-out infinite alternate;
    `}

  ${({ index }: TrophyProps) =>
    index === 0 &&
    css`
      left: -5%;
    `}
`;

type ConfettiProps = {
  readonly isVisible: boolean;
};
export const Confetti = styled.img<ConfettiProps>`
  font-size: 10vw;
  position: absolute;
  top: 22%;
  left: 0;
  right: 0;
  margin: auto;
  width: 100%;
  height: auto;
  visibility: ${({ isVisible }: ConfettiProps) =>
    isVisible ? "visible" : "hidden"};
`;

const rainbow = keyframes`
  100%,
  0% {
    color: rgb(255 0 0);
  }

  8% {
    color: rgb(255 127 0);
  }
  
  16% {
    color: rgb(255 255 0);
  }
  
  25% {
    color: rgb(127 255 0);
  }
  
  33% {
    color: rgb(0 255 0);
  }
  
  41% {
    color: rgb(0 255 127);
  }
  
  50% {
    color: rgb(0 255 255);
  }
  
  58% {
    color: rgb(0 127 255);
  }
  
  66% {
    color: rgb(0 0 255);
  }
  
  75% {
    color: rgb(127 0 255);
  }
  
  83% {
    color: rgb(255 0 255);
  }
  
  91% {
    color: rgb(255 0 127);
  }
`;

export const NeonAnimation = styled.div`
  box-sizing: border-box;
  width: 136%;
  height: 62%;
  left: -17%;
  margin: auto;
  background-repeat: no-repeat;
  font-size: 5vw;
  border-radius: 0.5em;
  border: 0.15em solid currentcolor;
  animation: ${rainbow} 2.5s linear;
  animation-iteration-count: infinite;
  box-shadow: currentcolor 0 0 0 0.2em inset, currentcolor 0 0 1em 0.3em inset,
    rgba(0, 0, 0, 60%) 1em 1em 0.4em inset, currentcolor 0 0 0 0.2em,
    currentcolor 0 0 1em 1em, rgba(0, 0, 0, 60%) 1em 1em 0.4em;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  mix-blend-mode: hue;
`;

export const ButtonBottomRight = styled(ButtonComponent)`
  position: absolute;
  right: 0;
  bottom: 0;
`;
